import { Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { BrowserRouter as Router } from "react-router-dom";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import { TableHeader } from "../../../interfaces";
import SimpleTableBody from "../../MultiHeaderTable/SimpleTableBody";
import { fileNameFormat } from "../../../common/services/Utilities";
import { CSVLink } from "react-csv";
import clsx from "clsx";
import LeavingFDICDialog from "../../LeavingFDICDialog";

const useStyles = makeStyles((theme: Theme) => ({
  multiHeaderTableInstProfile: {
    "& thead": {
      "& th, td": {
        textAlign: "left",
      },
    },
    "& tbody": {
      "& td": {
        textAlign: "left",
      },
    },
    "& tr:nth-of-type(even)": {
      backgroundColor: "#e8ecf3",
    },
    "& tr:nth-of-type(odd):hover": {
      backgroundColor: "transparent",
    },
  },
  button: {
    "height": 40,
    "fontSize": ".875rem",
    "fontWeight": 400,
    "lineHeight": 1.5,
    "width": "33%",
    "color": theme.palette.primary.dark,
    "border": `1px solid ${theme.palette.primary.light} `,
    "borderRadius": 4,
    "marginTop": 16,
    "marginBottom": 24,
    "backgroundColor": "#d8e6f6",
    "&.CSVLink": {
      "display": "block",
      "padding": "9px 16px",
      "&:focus, &:active, &:hover": {
        backgroundColor: "white",
        color: theme.palette.primary.main,
        textDecoration: "none",
      },
    },
  },
}));

export default function MSAInformation() {
  const classes = useStyles();

  const tableHeadData = [
    { label: "Branch Variables", key: "VARIABLE" },
    { label: "Brief Description", key: "DESCRIPTION" },
  ];
  const rows = [
    {
      VARIABLE: { value: "MSABR", styles: {} },
      DESCRIPTION: { value: "Metropolitan Statistical Areas", styles: {} },
    },

    {
      VARIABLE: { value: "MSANAMB", styles: {} },
      DESCRIPTION: { value: "Metropolitan Statistical Area Name", styles: {} },
    },
    {
      VARIABLE: { value: "CSABR", styles: {} },
      DESCRIPTION: { value: "Combined Statistical Areas", styles: {} },
    },
    {
      VARIABLE: { value: "CSANAMBR", styles: {} },
      DESCRIPTION: { value: "Combined Statistical Areas Name", styles: {} },
    },
    {
      VARIABLE: { value: "DIVISIONB", styles: {} },
      DESCRIPTION: { value: "Metropolitan Division Number", styles: {} },
    },
    {
      VARIABLE: { value: "CBSA_DIV_NAMB", styles: {} },
      DESCRIPTION: { value: "Metropolitan Division Name ", styles: {} },
    },
    {
      VARIABLE: { value: "METROBR", styles: {} },
      DESCRIPTION: { value: "Metropolitan Statistical Flag", styles: {} },
    },
    {
      VARIABLE: { value: "MICROBR", styles: {} },
      DESCRIPTION: { value: "Micropolitan Statistical Flag", styles: {} },
    },
    {
      VARIABLE: { value: "NECTABR", styles: {} },
      DESCRIPTION: {
        value: "New England City and Town Areas (Branch)",
        styles: {},
      },
    },
    {
      VARIABLE: { value: "NECNAMB", styles: {} },
      DESCRIPTION: {
        value: "New England City and Town Area Names (Branch)",
        styles: {},
      },
    },
  ];

  const tableHeaders: TableHeader[][] = [
    tableHeadData.map((value: any) => ({
      title: value.label,
      colSpan: 1,
      styles: {},
    })),
  ];
  return (
    <Router>
      <div className={classes.multiHeaderTableInstProfile}>
        <Typography variant="body2" color="primary">
          The Metropolitan Statistical Areas (MSAs) are based on the 2020
          Census. These areas correspond to the state / county / CBSA
          relationships as defined by the Census Bureau. For the purpose of the
          Summary of Deposits, MSAs reflect only the CBSA codes for metropolitan
          areas. Micropolitan Areas and Metropolitan Divisions are excluded from
          the MSA definition, however, all of the MSA related fields (see table
          below) are available in the SOD database and download.
        </Typography>
        <Typography variant="body2" color="primary">
          MSA Listings - Please note that all of the Summary of Deposit data
          on-line, back to 1994, have been repopulated based on these
          definitions codes. The following variables are available for each
          branch office:{" "}
        </Typography>

        <MultiHeaderTable
          tableHeaders={tableHeaders}
          tableBody={<SimpleTableBody tableData={rows}></SimpleTableBody>}
        />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CSVLink
            style={{ textAlign: "center" }}
            headers={tableHeadData}
            className={clsx(classes.button, "CSVLink")}
            filename={`${fileNameFormat("MSA_Listings")}.csv`}
            data={rows.map(row => ({
              VARIABLE: row.VARIABLE.value,
              DESCRIPTION: row.DESCRIPTION.value,
            }))}
            asyncOnClick={true}
          >
            Download MSA / CBSA Listings
          </CSVLink>
        </div>

        <Typography
          variant="body2"
          color="primary"
          style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          For additional information, visit the Census Bureau website at{" "}
          <LeavingFDICDialog
            webAddress={
              "https://www.census.gov/programs-surveys/metro-micro.html"
            }
            label={" https://www.census.gov/programs-surveys/metro-micro.html"}
            toolTipText={
              "https://www.census.gov/programs-surveys/metro-micro.html"
            }
            noHover
          />
        </Typography>

        <Typography variant="h3">
          Defining Metropolitan and Micropolitan Statistical Areas
        </Typography>
        <Typography variant="body2" color="primary">
          Metropolitan and micropolitan statistical areas (metro and micro
          areas) are geographic entities defined by the U.S. Office of
          Management and Budget (OMB) for use by Federal statistical agencies in
          collecting, tabulating, and publishing Federal statistics. The term
          "Core Based Statistical Area (CBSA)" is a collective term for both
          metro and micro areas. A metro area contains a core urban area of
          50,000 or more population, and a micro area contains an urban core of
          at least 10,000 (but less than 50,000) population. Each metro or micro
          area consists of one or more counties and includes the counties
          containing the core urban area, as well as any adjacent counties that
          have a high degree of social and economic integration (as measured by
          commuting to work) with the urban core.
        </Typography>
        <Typography variant="body2" color="primary">
          The 2020 standards provide that each CBSA must contain at least one
          urban area of 10,000 or more population
        </Typography>
      </div>
    </Router>
  );
}
